/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { useContext } from 'react';
import styled from 'styled-components';
import colors from 'styles/colors';
import fonts from 'styles/fonts';
import media from 'styles/media';
import sizes from 'styles/sizes';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { withRouter } from 'next/router';

import CloseIcon from 'components/svg/CloseIcon';
import { ALL, FILTER_MODE, QUALIFIERS } from 'contexts/CruiseSearchContext/constants';
import { CLOSE_FILTERS_BUTTON, SELECTED_FILTER_TITLE } from '../constants';

import { DEFAULT } from 'components/Scroller/constants';
import { withEnableDisableScroller } from 'components/Scroller/context';
import { SectionTitle } from '../components';
import { useFeatureFlags } from '../../../hooks/LaunchDarkly/useFeatureFlags';

export const ModalHeader = styled.div`
	display: flex;
	background-color: ${colors.primary.ice};
	position: sticky;
	width: 100%;
	top: 0;
	left: 0;
	z-index: 3;
	@media (min-width: calc(${sizes.media.tabletPortrait}px + 0.5px)) {
		border-radius: 5px 5px 0 0;
		position: absolute;
		padding: 20px;
	}
`;

export const ModalTitleBar = styled.div`
	width: 100%;
	display: flex;

	@media (max-width: calc(${sizes.media.tabletPortrait}px)) {
		padding: 20px 0px 40px 0px;
		flex-direction: column;
	}
`;

export const Title = styled(SectionTitle)`
	&.title {
		margin: 0;
		line-height: 30px;

		@media (min-width: calc(${sizes.media.tabletPortrait}px + 0.5px)) {
			display: none;
		}
	}
`;

export const HeaderOption = styled.button`
	font-family: ${fonts.primary.bold};
	font-size: 14px;
	line-height: 17px;
	letter-spacing: 0.01em;
	cursor: pointer;
	position: relative;

	color: ${colors.secondary.deepCove};
	transition: color 0.1s cubic-bezier(0, 0, 0, 0);

	&:before {
		content: '';
		position: absolute;
		bottom: 0;
		width: 100%;
		height: 1px;
		border-radius: 5px;

		background-color: ${colors.secondary.deepCove};
		transition: background-color 0.1s cubic-bezier(0, 0, 0, 0);
	}

	&:hover {
		color: ${colors.primary.ocean};
		&:before {
			background-color: ${colors.primary.ocean};
		}
	}

	${media.small`
    color: ${colors.primary.ocean};
    &:before {
      background-color: ${colors.primary.ice};
    }
  
    &:hover {
      color: ${colors.primary.ocean};
      &:before {
        background-color: ${colors.primary.ice};
      }
    }
  `}
	${(props) => (props.visible ? 'display: block!important;' : '')}
`;

export const CloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;

	path {
		transition: stroke 0.1s cubic-bezier(0, 0, 0, 0);
	}

	svg {
		padding-bottom: 0px;
	}

	&:hover {
		path {
			stroke: ${colors.primary.ocean};
		}
	}
`;

export const HeaderColumn = styled.div`
	display: flex;
`;

export const HeaderTitleMobile = styled.div`
	display: flex;
	padding-top: 20px;
	&:first-child button {
		@media (min-width: calc(${sizes.media.smallMaxWidth}px + 0.5px)) {
			display: none;
		}
	}
`;

export const TopHeader = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
`;

/**
 * Header component of the FiltersModal
 * Displays title, close button, and reset button for each filter
 * @param {function} onReset resets displayed filter
 * @param {string} title of filter
 */
function Header({ onReset, title, enableScroller }) {
	const {
		filterMode,
		filterOpen,
		searchApplied,
		closeFilters,
		resetQueryParamsObject,
		openFilters,
		resetSearchSelectingToSearchApplied,
		globalization,
		updatedUniqueDestinations,
		promotionApplied,
		setPromotionSelected,
	} = useContext(CruiseSearchContext);
	const flags = useFeatureFlags();

	const { filtersDictionary } = globalization?.dictionaries || {};
	const { actionModalReset, actionModalResetAll, actionAllFilters } = filtersDictionary;

	const isAllFilters = title === actionAllFilters;
	const reset = isAllFilters ? actionModalResetAll : actionModalReset;
	const onClose = () => {
		resetQueryParamsObject();
		closeFilters();
		resetSearchSelectingToSearchApplied();
		updatedUniqueDestinations(flags);
		setPromotionSelected(promotionApplied);
		enableScroller();
	};
	const onBack = () => {
		closeFilters();
		openFilters('all', FILTER_MODE.SINGLE_FILTER);
	};
	return (
		<ModalHeader>
			<ModalTitleBar>
				<TopHeader>
					<HeaderColumn>
						{filterMode !== FILTER_MODE.ALL_FILTERS ? (
							flags.showResetOnFilters ? (
								filterOpen !== QUALIFIERS && (
									<HeaderOption data-testid="reset-filters-button" visible onClick={onReset} type="button">
										{reset}
									</HeaderOption>
								)
							) : (
								filterOpen !== ALL && (
									<HeaderOption data-testid="all-filters-button" onClick={onBack} type="button">
										{actionAllFilters}
									</HeaderOption>
								)
							)
						) : (
							<HeaderOption data-testid="all-filters-button" onClick={onBack} type="button">
								{actionAllFilters}
							</HeaderOption>
						)}
					</HeaderColumn>
					<HeaderColumn align="right">
						{filterMode !== FILTER_MODE.ALL_FILTERS ? (
							<CloseButton onClick={onClose} id={CLOSE_FILTERS_BUTTON} type="button">
								<CloseIcon color={colors.secondary.deepCove} />
							</CloseButton>
						) : searchApplied[filterOpen] && filterOpen !== QUALIFIERS && flags.showResetOnFilters ? (
							<HeaderOption onClick={onReset} type="button">
								{reset}
							</HeaderOption>
						) : (
							<CloseButton onClick={onClose} id={CLOSE_FILTERS_BUTTON} type="button">
								<CloseIcon color={colors.secondary.deepCove} />
							</CloseButton>
						)}
					</HeaderColumn>
				</TopHeader>
				<HeaderTitleMobile>
					<Title id={SELECTED_FILTER_TITLE} title={title} className="title" />
				</HeaderTitleMobile>
			</ModalTitleBar>
		</ModalHeader>
	);
}

Header.propTypes = {
	onReset: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	enableScroller: PropTypes.func.isRequired,
};

export default withRouter(withEnableDisableScroller(Header, DEFAULT));
